/**
 * Document delivery page (書類交付).
 *
 * Upload layout matches 給与データ管理: detected fields on the left, a drop
 * area on the right. Several PDFs can be chosen at once and reviewed with the
 * same carousel as 現場の給与データ管理.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../SalaryDataUploadDownload/css/SalaryDataMain.css';
import '../SalaryDataUploadDownload/css/DataUpload.css';
import './css/DocumentDelivery.css';
import { DocumentReadResult } from './identifyDocument';
import { readDocumentPdf } from './readDocumentPdf';
import EmployeeService from '../../services/EmployeeService';
import ConstructionWorkerService from '../../services/ConstructionWorkerService';
import {
  findUnregisteredUploadedNames,
  formatUnregisteredNamesAlert,
} from '../../utils/constructionUploadNameCheck';
import { uploadEmployeeDocument, EmployeeDocumentItem } from '../../services/EmployeeDocumentService';
import { readUploadPeriod } from './documentUploadFields';
import { DOCUMENT_TYPES } from './documentTypes';
import { AdminDocumentList } from './AdminDocumentList';

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const PENDING_VALUE = 'ファイルから自動検出中...';

interface QueuedPdf {
  id: string;
  file: File;
  name: string;
  size: number;
  error?: string;
  result?: DocumentReadResult;
  reading: boolean;
}

interface UploadNotice {
  ok: boolean;
  message: string;
  detail: string;
}

const NOTICE_VISIBLE_MS = 4500;
const EMPTY_READ: DocumentReadResult = {
  status: 'unknown',
  documentTypeLabel: '',
  person: '',
  yearLabel: '',
  revisionLabel: '',
  message: 'PDFを読み取れませんでした。',
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function savedDetail(data: EmployeeDocumentItem | undefined): string {
  if (!data) return '';
  const period = data.revisionMonth > 0
    ? `令和${data.eraYear}年${data.revisionMonth}月`
    : `令和${data.eraYear}年`;
  return `${data.documentTypeLabel} / ${data.personName} / ${period}`;
}

function validatePdf(file: File): string | undefined {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  if (!isPdf) return 'PDFファイルのみアップロードできます';
  if (file.size > MAX_FILE_BYTES) return 'ファイルサイズは10MB以下にしてください';
  return undefined;
}

function showsRevisionMonth(entry: QueuedPdf | undefined): boolean {
  if (!entry || entry.reading || !entry.result) return true;
  const label = entry.result.documentTypeLabel.trim();
  if (!label) return true;
  return DOCUMENT_TYPES.some((definition) => definition.label === label && definition.usesRevisionMonth);
}

function personsIn(list: QueuedPdf[]): string[] {
  return list
    .map((item) => item.result?.person.trim() || '')
    .filter((name) => name.length > 0);
}

export const DocumentDeliveryMain: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const readRequest = useRef(0);
  const filesRef = useRef<QueuedPdf[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [files, setFiles] = useState<QueuedPdf[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [nameRegistrationAlerts, setNameRegistrationAlerts] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [notice, setNotice] = useState<UploadNotice | null>(null);
  const [activeTab, setActiveTab] = useState<'upload' | 'list'>('upload');
  const registeredNamesRef = useRef<string[] | null>(null);

  const loadRegisteredNames = useCallback(async (): Promise<string[]> => {
    if (registeredNamesRef.current) return registeredNamesRef.current;
    const [employees, workers] = await Promise.all([
      EmployeeService.getAllEmployees().catch(() => ({ success: false as const, data: [] as { name?: string }[] })),
      ConstructionWorkerService.getAllConstructionWorkers().catch(() => ({ success: false as const, data: [] as { name?: string }[] })),
    ]);
    const names = [
      ...(employees.success && employees.data ? employees.data : []),
      ...(workers.success && workers.data ? workers.data : []),
    ]
      .map((person) => person.name)
      .filter((name): name is string => Boolean(name?.trim()));
    registeredNamesRef.current = names;
    return names;
  }, []);

  const replaceFiles = useCallback((next: QueuedPdf[]) => {
    filesRef.current = next;
    setFiles(next);
  }, []);

  const refreshNameAlerts = useCallback(async (list: QueuedPdf[], requestId: number) => {
    const persons = personsIn(list);
    if (persons.length === 0) {
      if (readRequest.current === requestId) setNameRegistrationAlerts([]);
      return;
    }
    const registered = await loadRegisteredNames();
    if (readRequest.current !== requestId) return;
    setNameRegistrationAlerts(findUnregisteredUploadedNames(persons, registered));
  }, [loadRegisteredNames]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const acceptFiles = (list: File[]) => {
    if (list.length === 0) return;
    const requestId = readRequest.current + 1;
    readRequest.current = requestId;
    const queued: QueuedPdf[] = list.map((file, index) => {
      const error = validatePdf(file);
      return {
        id: `${requestId}-${index}-${file.name}`,
        file,
        name: file.name,
        size: file.size,
        error,
        reading: !error,
      };
    });
    replaceFiles(queued);
    setCurrentFileIndex(0);
    setNameRegistrationAlerts([]);
    setNotice(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    void readQueued(requestId, queued);
  };

  const readQueued = async (requestId: number, queued: QueuedPdf[]) => {
    for (const item of queued) {
      if (readRequest.current !== requestId) return;
      if (item.error) continue;
      if (!filesRef.current.some((entry) => entry.id === item.id)) continue;
      let result: DocumentReadResult;
      try {
        result = await readDocumentPdf(item.file);
      } catch {
        result = EMPTY_READ;
      }
      if (readRequest.current !== requestId) return;
      const next = filesRef.current.map((entry) => (
        entry.id === item.id ? { ...entry, reading: false, result } : entry
      ));
      replaceFiles(next);
      await refreshNameAlerts(next, requestId);
    }
  };

  const resetSelection = () => {
    readRequest.current += 1;
    replaceFiles([]);
    setCurrentFileIndex(0);
    setNameRegistrationAlerts([]);
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearFiles = () => {
    resetSelection();
    setNotice(null);
  };

  const removeFile = (id: string) => {
    const next = filesRef.current.filter((entry) => entry.id !== id);
    replaceFiles(next);
    setCurrentFileIndex((index) => (index >= next.length ? Math.max(0, next.length - 1) : index));
    void refreshNameAlerts(next, readRequest.current);
    if (next.length === 0 && fileInputRef.current) fileInputRef.current.value = '';
  };

  const current = files.length > 0
    ? files[Math.min(currentFileIndex, files.length - 1)]
    : undefined;
  const anyReading = files.some((entry) => entry.reading);
  const readyFiles = files.filter((entry) => !entry.reading && !entry.error && entry.result && readUploadPeriod(entry.result));

  const note = !current
    ? 'PDFを選択すると、書類の種類・対象者・年月を読み取ります。'
    : anyReading
      ? '読み取り中...'
      : current.error || current.result?.message || '';

  const uploadDocuments = async () => {
    if (readyFiles.length === 0 || anyReading || isUploading) return;
    if (readyFiles.length > 1 && !window.confirm(`${readyFiles.length}件のファイルをアップロードしますか？`)) return;
    setIsUploading(true);
    setNotice(null);
    const total = files.length;
    let saved = 0;
    const savedIds = new Set<string>();
    let singleMessage = '';
    let singleDetail = '';
    let firstError = '';
    for (const item of readyFiles) {
      const period = readUploadPeriod(item.result!);
      if (!period) continue;
      const result = await uploadEmployeeDocument({
        file: item.file,
        documentType: item.result!.documentTypeLabel,
        personName: item.result!.person,
        eraYear: period.eraYear,
        revisionMonth: period.revisionMonth,
      });
      if (result.success) {
        saved += 1;
        savedIds.add(item.id);
        singleMessage = result.message || '書類を保存しました。';
        singleDetail = savedDetail(result.data);
      } else if (!firstError) {
        firstError = result.message || '書類の保存に失敗しました。';
      }
    }
    if (saved === readyFiles.length) {
      const oneFile = readyFiles.length === 1 && total === 1;
      resetSelection();
      setNotice({
        ok: true,
        message: oneFile ? singleMessage : `${saved}/${total}件の書類を保存しました。`,
        detail: oneFile ? singleDetail : '',
      });
      return;
    }
    const remaining = filesRef.current.filter((entry) => !savedIds.has(entry.id));
    replaceFiles(remaining);
    setCurrentFileIndex((index) => (index >= remaining.length ? Math.max(0, remaining.length - 1) : index));
    setIsUploading(false);
    void refreshNameAlerts(remaining, readRequest.current);
    setNotice({
      ok: saved > 0,
      message: saved > 0 ? `${saved}/${total}件の書類を保存しました。` : firstError,
      detail: '',
    });
  };

  const displayValue = (value: string | undefined) => {
    if (!current || current.error) return '';
    if (current.reading || !current.result) return PENDING_VALUE;
    return value || '';
  };

  const fileCard = (entry: QueuedPdf) => (
    <>
      <div className="file-info">
        <div className="file-icon">📄</div>
        <div className="file-details">
          <div className="file-name">{entry.name}</div>
          <div className="file-meta">{formatFileSize(entry.size)} • application/pdf</div>
          {entry.error && <div className="file-error">{entry.error}</div>}
          {entry.reading && <div className="parsing-message">読み取り中...</div>}
        </div>
      </div>
      <div className="file-actions">
        <button
          type="button"
          className="remove-button"
          onClick={(event) => {
            event.stopPropagation();
            removeFile(entry.id);
          }}
          disabled={isUploading}
          title="ファイルを削除"
        >
          🗑️
        </button>
      </div>
    </>
  );

  return (
    <div className="main-content document-delivery-page">
      <h3>書類交付</h3>
      <nav className="sub-tab-navigation" aria-label="書類交付">
        <button
          type="button"
          className={`sub-tab-button ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          データアップロード
        </button>
        <button
          type="button"
          className={`sub-tab-button ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          一覧
        </button>
      </nav>
      {activeTab === 'list' ? (
        <AdminDocumentList />
      ) : (
      <>
      <p className="document-delivery-intro">
        基準報酬決定通知書と給与所得の源泉徴収票のPDFを登録します。源泉徴収票はPDFの文字から読み取ります。基準報酬決定通知書のスキャンは見出しを確認し、対象者と年月はファイル名（氏名_日付_番号.pdf）から読み取ります。
      </p>

      <div className="data-upload-container">
        <div className="upload-layout">
          <div className="left-panel">
            <div className="selectors-section">
              <div className="selector-group">
                <label htmlFor="document-type-display">書類の種類:</label>
                <input
                  id="document-type-display"
                  type="text"
                  value={displayValue(current?.result?.documentTypeLabel)}
                  readOnly
                  className="year-display"
                  placeholder="ファイルから自動検出"
                />
              </div>
              <div className="selector-group">
                <label htmlFor="document-person-display">対象者:</label>
                <input
                  id="document-person-display"
                  type="text"
                  value={displayValue(current?.result?.person)}
                  readOnly
                  className="year-display"
                  placeholder="ファイルから自動検出"
                />
              </div>
              <div className="selector-group">
                <label htmlFor="document-year-display">年:</label>
                <input
                  id="document-year-display"
                  type="text"
                  value={displayValue(current?.result?.yearLabel)}
                  readOnly
                  className="year-display"
                  placeholder="ファイルから自動検出"
                />
              </div>
              {showsRevisionMonth(current) && (
                <div className="selector-group">
                  <label htmlFor="document-revision-display">改定年月:</label>
                  <input
                    id="document-revision-display"
                    type="text"
                    value={displayValue(current?.result?.revisionLabel)}
                    readOnly
                    className="month-display"
                    placeholder="基準報酬決定通知書のみ"
                  />
                </div>
              )}
            </div>

            <div className="buttons-section">
              <button
                type="button"
                className="upload-btn"
                onClick={uploadDocuments}
                disabled={readyFiles.length === 0 || anyReading || isUploading}
              >
                {isUploading ? 'アップロード中...' : (files.length > 1 ? '一括アップロード' : 'アップロード')}
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={clearFiles}
                disabled={files.length === 0 || isUploading}
              >
                キャンセル
              </button>
              <p className="document-delivery-note">{note}</p>
            </div>
          </div>

          <div className="right-panel">
            <div className="upload-section">
              <div
                className={`upload-area ${isDragOver ? 'drag-over' : ''} ${files.length > 0 ? 'has-file' : ''}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  setIsDragOver(false);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDragOver(false);
                  acceptFiles(Array.from(event.dataTransfer.files));
                }}
                onClick={() => files.length === 0 && fileInputRef.current?.click()}
              >
                {files.length === 0 ? (
                  <div className="upload-content">
                    <div className="upload-icon">📁</div>
                    <h4>PDFをここにアップロードしてください</h4>
                    <p>ファイルをドラッグ＆ドロップまたはクリックして選択（複数可）</p>
                    <div className="supported-formats">
                      <span>対応形式: PDFファイルのみ</span>
                      <span>最大サイズ: 10MB</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="file-display file-carousel">
                      {files.length > 1 && (
                        <button
                          type="button"
                          className="carousel-arrow carousel-arrow-left"
                          onClick={(event) => {
                            event.stopPropagation();
                            setCurrentFileIndex((index) => Math.max(0, index - 1));
                          }}
                          disabled={currentFileIndex === 0}
                          aria-label="前のファイル"
                        >
                          ◀
                        </button>
                      )}
                      <div className="file-display-inner">
                        {current ? fileCard(current) : null}
                      </div>
                      {files.length > 1 && (
                        <button
                          type="button"
                          className="carousel-arrow carousel-arrow-right"
                          onClick={(event) => {
                            event.stopPropagation();
                            setCurrentFileIndex((index) => Math.min(files.length - 1, index + 1));
                          }}
                          disabled={currentFileIndex >= files.length - 1}
                          aria-label="次のファイル"
                        >
                          ▶
                        </button>
                      )}
                    </div>
                    <div className="upload-area-footer">
                      <p className="multi-file-message">
                        {files.length}件のファイルがアップロードの準備ができています。アップロード前に各ファイルの詳細を確認してください。
                      </p>
                      {files.length > 1 && (
                        <p className="carousel-index">
                          ファイル {Math.min(currentFileIndex, files.length - 1) + 1} / {files.length}
                        </p>
                      )}
                    </div>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  onChange={(event) => {
                    acceptFiles(Array.from(event.target.files ?? []));
                  }}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {nameRegistrationAlerts.length > 0 && (
              <div className="data-exists-warning" role="status">
                <div className="warning-icon">⚠️</div>
                <div className="warning-message">
                  <strong>登録アカウントとの氏名確認</strong>
                  <p style={{ margin: '8px 0 0' }}>
                    {formatUnregisteredNamesAlert(nameRegistrationAlerts)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </>
      )}
      {notice && (
        <div
          className={`document-delivery-popup ${notice.ok ? 'success' : 'error'}`}
          role={notice.ok ? 'status' : 'alert'}
        >
          <p>{notice.message}</p>
          {notice.detail ? <p>{notice.detail}</p> : null}
        </div>
      )}
    </div>
  );
};
